import { faChevronRight, faCircleQuestion, faTrophy } from '@fortawesome/free-solid-svg-icons'
import * as sound from '../sound'
import { muteRespinWarning, respinWarningMuted } from '../respinWarning'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import { Dialog } from '~/components/dialog'
import { Page } from '~/components/Page'
import { ACCENT, EmptyBlock, ErrorBlock, OutlineButton, Panel, SectionHeading } from '~/components/ui'
import { useModal } from '~/hooks/useModal'
import {
    errorCopy,
    keepSession,
    listPacks,
    openPack,
    Pack,
    PackPool,
    PackSession,
    PacksApiError,
    respinSession,
} from '../api/packs'
import { HowItWorksModal } from '../components/HowItWorksModal'
import { InsufficientFundsModal } from '../components/InsufficientFundsModal'
import { usePacksGate } from '../hooks/usePacksGate'
import { ClaimIndicator } from '../components/ClaimIndicator'
import { LiveFeed } from '../components/LiveFeed'
import { PackOpening } from '../components/PackOpening'
import { ACTION_ROW_H, PackStage } from '../components/PackStage'
import { TopSkinsModal } from '../components/TopSkinsModal'

const PRIZE_GREEN = '#00FF90'
/** How long the claimed weapon fades out for on the way back to the catalogue.
 *  The wait before it is not a number here any more: `ClaimIndicator` runs the
 *  spinner and draws the tick, and the fade starts when the tick finishes. Two
 *  timelines for one moment is how they drift apart. */
const CLAIMED_FADE_MS = 500

const gradientCta =
    'inline-flex min-h-[3rem] items-center justify-center px-8 text-sm font-black uppercase tracking-[0.2em] transition-[filter] hover:brightness-110 disabled:opacity-40 disabled:hover:brightness-100'

/** Bottom-left info buttons (See top skins / How it works).
 *
 *  `ACTION_ROW_H` rather than vertical padding: these two are the rail's half
 *  of the alignment `PackStage` documents. Both blocks sit at the bottom of the
 *  same grid row with the same two row heights, which is what puts Spin level
 *  with "See top skins" at any viewport height. */
const infoButton = `flex ${ACTION_ROW_H} cursor-pointer items-center gap-3 border border-white/10 bg-white/[0.03] px-4 text-xs font-black uppercase tracking-[0.15em] text-white/70 transition-colors hover:border-white/25 hover:text-white`

/** One row of the left-hand pack selector. Highlighted when it is the pack on
 *  the centre stage — the cyan edge is the site's active-tab marker. */
const PackRow = ({ pack, selected, onSelect }: { pack: Pack; selected: boolean; onSelect: () => void }) => {
    const soldOut = pack.is_enabled && !pack.in_stock
    return (
        <button
            onClick={onSelect}
            className={twMerge(
                'flex w-full items-center justify-between gap-2 border-l-2 px-3 py-2.5 text-left transition-colors',
                selected
                    ? 'border-[#03CEFF] bg-white/[0.06]'
                    : 'border-transparent hover:bg-white/[0.03]',
            )}
        >
            <span className='flex min-w-0 flex-col'>
                <span
                    className={twMerge(
                        'truncate text-xs font-black uppercase tracking-[0.12em]',
                        selected ? 'text-white' : 'text-white/70',
                    )}
                >
                    {pack.name}
                </span>
                <span className='text-2xs font-bold uppercase tracking-[0.15em] text-white/35'>
                    {/* No "coming soon": the server does not send switched-off
                        packs at all, because a dead row in a list of things you
                        can open promises something that may never ship. */}
                    {soldOut
                        ? 'Sold out'
                        : // A skin can be granted any number of times, so a skin
                          // pack with no supply set never runs out. `stock` carries
                          // a sentinel for that and is not a number to show.
                          pack.unlimited
                          ? 'Unlimited'
                          : `${pack.stock} left`}
                </span>
            </span>
            <span className='shrink-0 text-sm font-black tabular-nums text-white/80'>
                {pack.price}
                <span className='ml-1 text-2xs text-white/40'>{pack.currency}</span>
            </span>
        </button>
    )
}

/**
 * The Packs page.
 *
 * Laid out as a rip screen: a live pulls ticker across the top, the pack
 * catalogue as a left-hand selector, and the chosen pack centre stage. Opening
 * a pack swaps the centre stage for the reveal; the shop stays hidden until the
 * player has kept or opened again, because an undecided weapon is money
 * committed and a second purchase before finishing the first is a trap.
 */
export const PacksPage = () => {
    const [packs, setPacks] = useState<Pack[]>([])
    const [selectedId, setSelectedId] = useState<number | null>(null)
    const [session, setSession] = useState<PackSession | null>(null)
    const [error, setError] = useState<string | null>(null)
    // The one refusal with somewhere to go. Kept apart from `error` so the
    // message can carry a link rather than a sentence telling somebody to
    // find the page themselves.
    const [needsWallet, setNeedsWallet] = useState(false)
    const [notice, setNotice] = useState<string | null>(null)
    // Decoys, not the drawable pool: this only feeds the reel strip. See
    // `PackListView.pools` for why those are two different sets.
    const [decoys, setDecoys] = useState<PackPool>({ weapon_types: [], rarities: [], erbs: [] })
    const [revealed, setRevealed] = useState(false)
    const [busy, setBusy] = useState(false)
    // Whether this account may buy at all, so the action is dead before the press rather than
    // after the charge. Same answer the open endpoint refuses with.
    const { blockedReason } = usePacksGate()
    // Which session is being asked "are you sure" about. The respin is the one
    // action on this page that destroys something the player is looking at, and
    // it used to fire straight off the button.
    const [confirmRespin, setConfirmRespin] = useState<any | null>(null)
    const [dontRemind, setDontRemind] = useState(false)

    const [loaded, setLoaded] = useState(false)
    const [revealKey, setRevealKey] = useState(0)
    // Bumped after a keep so the live feed reloads and shows the fresh pull.
    const [feedKey, setFeedKey] = useState(0)
    // The cinematic launch: on spin, the side UI fades and the pack takes centre
    // stage before the reveal begins.
    const [launching, setLaunching] = useState(false)

    // The music bed, and Slabz's rule about when it is NOT playing.
    //
    // Their arena suppresses the lobby loop for the whole of a rip -- the
    // purchase, the reveal overlay, and any transition between -- because the
    // reel and the reveal carry their own sound design and a loop underneath
    // just muddies them. Same here: the bed ducks out the moment a pack starts
    // opening and fades back in when the player is returned to the stage.
    useEffect(() => {
        if (session || launching) sound.stopMusic()
        else sound.playMusic()
    }, [session, launching])

    // And never leave it playing behind another page.
    useEffect(() => () => sound.stopMusic(), [])

    // The fade-out on the way back to the catalogue.
    const [leaving, setLeaving] = useState(false)
    const { setModal } = useModal()

    const refresh = useCallback(async () => {
        try {
            setPacks(await listPacks())
            setError(null)
        } catch (e) {
            const err = e as PacksApiError
            setNeedsWallet(err.code === 'no_delivery_wallet')
            setError(errorCopy(err.code, err.message))
        } finally {
            setLoaded(true)
        }
    }, [])

    useEffect(() => {
        refresh()
    }, [refresh])

    // Keep a valid selection: default to the first available pack, or the first
    // pack if none are available, whenever the catalogue changes.
    useEffect(() => {
        if (packs.length === 0) return
        if (selectedId !== null && packs.some((p) => p.id === selectedId)) return
        const firstAvailable = packs.find((p) => p.is_enabled && p.in_stock)
        setSelectedId((firstAvailable ?? packs[0]).id)
    }, [packs, selectedId])

    const selectedPack = useMemo(
        () => packs.find((p) => p.id === selectedId) ?? null,
        [packs, selectedId],
    )

    const act = async (
        fn: () => Promise<PackSession>,
        // What it costs, so a payment failure can offer a prefilled deposit.
        fundsNeeded?: { amount: string; currency: string },
    ) => {
        setBusy(true)
        setError(null)
        setNeedsWallet(false)
        setNotice(null)
        try {
            const result = await fn()
            // Replay the reveal on every open/respin (and on the race re-roll),
            // but NOT on a successful keep -- keep returns the same weapon with a
            // new status, so re-running the reels over it reads as a glitch.
            //
            // This must key off the action, not the mint: respin draws from a
            // pool that still contains the current weapon (it isn't claimed until
            // keep), so it can legitimately redraw the same one. Comparing mints
            // then skipped the replay and the respin looked dead.
            const isSuccessfulKeep = result.kept === true
            setSession(result)
            if (!isSuccessfulKeep) {
                setRevealed(false)
                setRevealKey((n) => n + 1)
            }
            if (result.kept === true) {
                setFeedKey((n) => n + 1)
                // Only a keep changes drawable stock, so only then re-read the
                // catalogue. Open/respin hold a weapon without claiming it, so
                // the pack list is unchanged and re-reading it would be waste.
                await refresh()
            }
            if (result.kept === false) {
                setNotice(
                    `${
                        result.previous_asset?.name ?? 'That weapon'
                    } was claimed by another player first, so you were given a new one. You were not charged.`,
                )
            }
        } catch (e) {
            const err = e as PacksApiError
            // Not enough balance: swap the dead error for a deposit prompt with
            // the exact amount, prefilled on the way to the cashier.
            if (err.code === 'payment_failed' && fundsNeeded) {
                setModal(<InsufficientFundsModal amount={fundsNeeded.amount} currency={fundsNeeded.currency} />)
            } else {
                setNeedsWallet(err.code === 'no_delivery_wallet')
            setError(errorCopy(err.code, err.message))
            }
            // Bring the catalogue back if the spin never landed.
            setLaunching(false)
        } finally {
            setBusy(false)
        }
    }

    /** Spin a pack, cinematically: charge + draw first, then — only if it landed
     *  — fade the side UI and reveal. Opening first means an insufficient-funds
     *  spin goes straight to the deposit prompt without the animation ever
     *  starting. */
    const spin = async (pack: Pack) => {
        setBusy(true)
        setError(null)
        setNeedsWallet(false)
        setNotice(null)
        setDecoys(pack.decoys)
        try {
            const result = await openPack(pack.id)
            setLaunching(true)
            // Let the launch play before the reveal takes the stage.
            window.setTimeout(() => {
                setSession(result)
                setRevealed(false)
                setRevealKey((n) => n + 1)
                setBusy(false)
            }, 550)
        } catch (e) {
            const err = e as PacksApiError
            if (err.code === 'payment_failed') {
                setModal(<InsufficientFundsModal amount={pack.price} currency={pack.currency} />)
            } else {
                setNeedsWallet(err.code === 'no_delivery_wallet')
            setError(errorCopy(err.code, err.message))
            }
            setBusy(false)
        }
    }

    /** Return to the catalogue and restore the side UI. */
    const backToCatalogue = () => {
        setSession(null)
        setNotice(null)
        setLaunching(false)
        setLeaving(false)
    }

    const finished = session?.kept === true
    const deciding = session !== null && !finished

    // Once the pull is claimed and shown, hold the confirmation, then bring the
    // pack list back so the player can pick another. The kept weapon has already
    // surfaced in the live feed by then.
    //
    // This used to sit beside an "open another" button and could afford to be
    // short. That button is gone -- it was a third block on a screen the card
    // was already losing height to -- so this is now the only way back and is
    // paced for reading rather than for dismissing.
    useEffect(() => {
        if (!leaving) return
        const back = setTimeout(() => backToCatalogue(), CLAIMED_FADE_MS)
        return () => clearTimeout(back)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leaving])

    return (
        <Page title='Packs' header='Packs'>
            {/* The whole rip screen inside one viewport, the way the reference
                plays: choosing a pack is a glance between the rail, the pack and
                the price, and a page that scrolls turns that glance into
                navigation. Bounded only from `lg` up -- the single-column phone
                layout has more to say than a phone screen holds, and squeezing
                it to fit would shrink the pack to a thumbnail.

                The two chrome variables are published by the menu and banner's
                own ResizeObservers, so a wrapped nav row or a promo banner
                takes its space out of this height rather than pushing the page
                past the fold. */}
            <div className='mx-auto mb-16 flex w-full max-w-[1500px] flex-col gap-5 px-4 pt-5 md:mb-24 md:gap-6 md:pt-6 lg:mb-0 lg:h-[calc(100dvh_-_var(--page-menu-height,4rem)_-_var(--page-banner-height,0px))] lg:gap-4 lg:overflow-hidden lg:pb-4 lg:pt-4'>
                {/* Live pulls ticker, while choosing only. During the opening the
                    card needs that height more than the page needs other players'
                    pulls -- and the reveal is the one moment the screen should be
                    about your own. It reloads on remount, so nothing is missed by
                    hiding it. */}
                {!session && <LiveFeed refreshKey={feedKey} />}

                {/* A missing wallet is the one error with a next step, so it
                    gets a link to the page that fixes it rather than a sentence
                    telling somebody to go and find it. */}
                {error && needsWallet && (
                    <div className='border border-amber-400/40 bg-amber-400/[0.08] px-4 py-3 text-xs font-semibold uppercase leading-relaxed tracking-[0.08em] text-amber-200'>
                        Connect a Solana wallet to your{' '}
                        <Link to='/account' className='underline hover:text-white'>
                            account
                        </Link>{' '}
                        before opening this pack. The weapon is sent on-chain, so there has to be
                        somewhere to send it.
                    </div>
                )}

                {notice && (
                    <div className='border border-amber-400/40 bg-amber-400/[0.08] px-4 py-3 text-xs font-semibold uppercase leading-relaxed tracking-[0.08em] text-amber-200'>
                        {notice}
                    </div>
                )}

                {!session ? (
                    error ? (
                        <ErrorBlock onRetry={refresh}>{error}</ErrorBlock>
                    ) : loaded && packs.length === 0 ? (
                        <EmptyBlock title='No packs available'>
                            There is nothing to open right now. Check back soon.
                        </EmptyBlock>
                    ) : (
                        <div className='grid grid-cols-1 gap-5 lg:min-h-0 lg:flex-1 lg:grid-cols-[260px,1fr,260px] lg:gap-6'>
                            {/* LEFT — pack selector, with the info buttons pinned to
                                the bottom. Each opens a modal. Fades out on spin. */}
                            <aside
                                className={twMerge(
                                    'flex min-h-0 animate-slide-in-left flex-col gap-4 transition-all duration-500',
                                    launching && 'pointer-events-none -translate-x-4 opacity-0',
                                )}
                            >
                                <SectionHeading title='Choose Pack' hint='01' />
                                {/* Scrolls inside the rail once the catalogue
                                    outgrows it, so a long list costs the page
                                    its own height rather than the viewport's. */}
                                <Panel className='flex min-h-0 flex-col overflow-y-auto py-1'>
                                    {packs.map((pack) => (
                                        <PackRow
                                            key={pack.id}
                                            pack={pack}
                                            selected={pack.id === selectedId}
                                            onSelect={() => setSelectedId(pack.id)}
                                        />
                                    ))}
                                </Panel>

                                {selectedPack && (
                                    <div className='mt-auto flex flex-col gap-2 pt-2'>
                                        <Dialog>
                                            <Dialog.Trigger>
                                                <div className={infoButton}>
                                                    <FontAwesomeIcon icon={faTrophy} className='text-[#03CEFF]' />
                                                    See top skins
                                                    <FontAwesomeIcon
                                                        icon={faChevronRight}
                                                        className='ml-auto text-2xs text-white/30'
                                                    />
                                                </div>
                                            </Dialog.Trigger>
                                            <Dialog.Content>
                                                <TopSkinsModal
                                                    packId={selectedPack.id}
                                                    packName={selectedPack.name}
                                                />
                                            </Dialog.Content>
                                        </Dialog>
                                        <Dialog>
                                            <Dialog.Trigger>
                                                <div className={infoButton}>
                                                    <FontAwesomeIcon
                                                        icon={faCircleQuestion}
                                                        className='text-white/50'
                                                    />
                                                    How it works
                                                    <FontAwesomeIcon
                                                        icon={faChevronRight}
                                                        className='ml-auto text-2xs text-white/30'
                                                    />
                                                </div>
                                            </Dialog.Trigger>
                                            <Dialog.Content>
                                                <HowItWorksModal />
                                            </Dialog.Content>
                                        </Dialog>
                                    </div>
                                )}
                            </aside>

                            {/* CENTRE — the pack, front and centre. On spin it drops
                                and scales up to take the stage as the sides fade. */}
                            {/* The pack itself stays put through the launch — only the
                                surrounding UI fades. */}
                            <main className='flex min-h-0 animate-fade-in items-stretch justify-center'>
                                {selectedPack && (
                                    <PackStage
                                        pack={selectedPack}
                                        busy={busy || launching}
                                        launching={launching}
                                        onOpen={() => spin(selectedPack)}
                                        blockedReason={blockedReason}
                                    />
                                )}
                            </main>

                            {/* RIGHT — an equal-width spacer that balances the rail so
                                the pack lands on the true centre. */}
                            <div className='hidden lg:block' aria-hidden='true' />
                        </div>
                    )
                ) : (
                    session.asset && (
                        // Same bounded height as the catalogue view, so the reveal cannot
                        // overrun the page either. `min-h-0` is what lets the card inside
                        // actually shrink -- without it a flex child refuses to go below
                        // its content size, and the buttons at the bottom are the part
                        // that gets clipped.
                        <div
                            className={twMerge(
                                'flex flex-col items-center gap-5 pt-2 transition-opacity lg:min-h-0 lg:flex-1',
                                leaving && 'opacity-0',
                            )}
                            style={{ transitionDuration: `${CLAIMED_FADE_MS}ms` }}
                        >
                            {error && !needsWallet && (
                                <div className='w-full max-w-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-xs font-semibold uppercase tracking-[0.08em] text-red-300'>
                                    {error}
                                </div>
                            )}

                            <PackOpening
                                weapon={session.asset}
                                decoys={decoys}
                                revealKey={revealKey}
                                onFinished={() => setRevealed(true)}
                            />

                            {/* Claiming turns this same button into the
                                confirmation rather than opening a panel below it.
                                The panel was a third block competing for a screen
                                that already carries the card, the slots and the
                                traits, and the card was what got squeezed. */}
                            {/* Always mounted, hidden until the reveal. Rendered
                                only when revealed, this row appeared mid-sequence
                                and took its height out of the card above it -- so
                                the card was one size face-down and a smaller one
                                face-up, and the flip looked like a resize.
                                Reserving the space keeps the card a single size
                                the whole way through. */}
                            <div
                                className={twMerge(
                                    'flex w-full max-w-md shrink-0 flex-col items-center gap-4',
                                    revealed ? 'animate-fade-in' : 'invisible',
                                )}
                            >
                                    {finished ? (
                                        <>
                                            <button
                                                disabled
                                                style={{
                                                    borderColor: PRIZE_GREEN,
                                                    color: PRIZE_GREEN,
                                                    textShadow: '0 0 24px rgba(0,255,144,0.35)',
                                                }}
                                                className={`w-full gap-3 border ${gradientCta} disabled:opacity-100`}
                                            >
                                                <ClaimIndicator
                                                    color={PRIZE_GREEN}
                                                    onDone={() => setLeaving(true)}
                                                />
                                                {session.delivery?.status === 'Delivered'
                                                    ? 'Sent to your wallet'
                                                    : 'Weapon claimed'}
                                            </button>
                                            {/* One line, not a panel: where it went
                                                still matters, and this is the only
                                                place it is said. */}
                                            {session.delivery && (
                                                <span className='text-2xs font-bold uppercase tracking-[0.15em] text-white/40'>
                                                    {session.delivery.status} ·{' '}
                                                    <span className='font-mono normal-case'>
                                                        {session.delivery.address.slice(0, 6)}…
                                                        {session.delivery.address.slice(-6)}
                                                    </span>
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        // Keep it, or spin again -- as many times
                                        // as they like.
                                        //
                                        // The one-respin cap lived HERE and only
                                        // here: after a single roll this branch
                                        // swapped both buttons for a lone
                                        // "Collect". `PackService.respin` has
                                        // never had a limit -- its docstring
                                        // reasons about "a long run of respins"
                                        // costing the same each time -- so the
                                        // rule was a screen refusing to offer
                                        // something the server would have done.
                                        <div className='flex w-full flex-col gap-3 sm:flex-row'>
                                            <button
                                                onClick={() => act(() => keepSession(session.session_id))}
                                                disabled={busy}
                                                style={{ background: ACCENT }}
                                                className={`flex-1 ${gradientCta}`}
                                            >
                                                Keep it
                                            </button>
                                            <OutlineButton
                                                onClick={() => {
                                                    // The warning carries the
                                                    // "you are giving this up"
                                                    // part, so the button stays
                                                    // the short, ordinary word.
                                                    // Muted, it fires straight
                                                    // through.
                                                    if (respinWarningMuted()) {
                                                        act(
                                                            () =>
                                                                respinSession(
                                                                    session.session_id,
                                                                ),
                                                            {
                                                                amount: session.respin_price,
                                                                currency:
                                                                    selectedPack?.currency ?? '',
                                                            },
                                                        )
                                                    } else {
                                                        setConfirmRespin(session)
                                                    }
                                                }}
                                                disabled={busy}
                                                className='flex-1'
                                            >
                                                Respin &middot; ${session.respin_price}
                                            </OutlineButton>
                                        </div>
                                    )}
                            </div>

                        </div>
                    )
                )}
            </div>

            {/* Naming the weapon, not just warning about one. "You will lose
                your current item" is a sentence; "You will lose Vandal | Neon
                Riot" is a decision. */}
            {confirmRespin && (
                <div
                    className='fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4'
                    onClick={() => setConfirmRespin(null)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className='flex w-full max-w-sm flex-col gap-3 rounded border border-white/20 bg-zinc-900 p-4 text-sm'
                    >
                        <div className='font-bold uppercase tracking-widest text-white/80'>
                            Give up this pull?
                        </div>
                        <div className='text-white/60'>
                            <span className='text-white'>
                                {confirmRespin.current_skin?.name ??
                                    confirmRespin.current_asset?.name ??
                                    'This item'}
                            </span>{' '}
                            goes back in the pool and you draw again. You cannot get it back
                            except by drawing it a second time.
                        </div>
                        <div className='text-white/60'>
                            Spinning again costs{' '}
                            <span className='text-white'>${confirmRespin.respin_price}</span>. You
                            can keep spinning as many times as you like.
                        </div>
                        <div className='flex flex-row items-center justify-between gap-3'>
                            {/* Left, away from the two actions, so it cannot be
                                hit while reaching for either one. */}
                            <label className='flex cursor-pointer flex-row items-center gap-2 text-xs text-white/50 hover:text-white/80'>
                                <input
                                    type='checkbox'
                                    checked={dontRemind}
                                    onChange={(e) => setDontRemind(e.target.checked)}
                                />
                                <span>Don&rsquo;t remind me</span>
                            </label>
                            <div className='flex flex-row items-center gap-2'>
                            <button
                                type='button'
                                onClick={() => setConfirmRespin(null)}
                                className='px-3 py-1 text-xs underline opacity-60'
                            >
                                Keep it instead
                            </button>
                            <button
                                type='button'
                                disabled={busy}
                                onClick={() => {
                                    const s = confirmRespin
                                    muteRespinWarning(dontRemind)
                                    setConfirmRespin(null)
                                    act(() => respinSession(s.session_id), {
                                        amount: s.respin_price,
                                        currency: selectedPack?.currency ?? '',
                                    })
                                }}
                                className='rounded border border-red-400/60 px-3 py-1 text-xs text-red-400 disabled:opacity-30'
                            >
                                Give it up, respin
                            </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Page>
    )
}
